import React, { memo } from "react";
import { AsFormItemLayout } from "./AsFormItemLayout";
import type { IFormItemLayout, TDirector, TAuthorisedSignatory } from "@adgm/cross-models";
import { KycDirectorsTable } from "./KycDirectorsTable";
import { KycLabelElement } from "./KycLabelElement";
import { isEqual } from "lodash-es";

interface IProps extends IFormItemLayout<(TDirector | TAuthorisedSignatory)[]> {
  value?: (TDirector | TAuthorisedSignatory)[];
  onChange: (value: (TDirector | TAuthorisedSignatory)[]) => void;
  /** 是否为授权签字人，默认为 false（董事） */
  isAuthorisedSignatory?: boolean;
}

/**
 * 自定义比较函数，用于优化memo性能
 * @description 对value数组进行深度比较，其他props使用浅比较
 * @param prevProps - 前一次props
 * @param nextProps - 下一次props
 * @returns boolean - 是否相等
 */
const areEqual = (prevProps: IProps, nextProps: IProps) => {
  return (
    isEqual(prevProps.value, nextProps.value) &&
    prevProps.label === nextProps.label &&
    prevProps.error === nextProps.error &&
    prevProps.sequenceNumber === nextProps.sequenceNumber &&
    prevProps.tip === nextProps.tip &&
    prevProps.hideErrorSpace === nextProps.hideErrorSpace &&
    prevProps.required === nextProps.required &&
    prevProps.style === nextProps.style &&
    prevProps.isAuthorisedSignatory === nextProps.isAuthorisedSignatory
  );
};

/**
 * 董事和授权签字人表单组件
 * 用于输入董事和授权签字人的基本信息
 */
export const DirectorsForm: React.FC<IProps> = memo(
  ({
    value = [],
    onChange,
    error,
    label,
    tip,
    hideErrorSpace,
    sequenceNumber,
    style,
    required,
    isAuthorisedSignatory = false,
  }) => {
    return (
      <AsFormItemLayout
        label={label}
        error={error}
        tip={tip}
        hideErrorSpace={hideErrorSpace}
        required={required}
        labelElement={
          <KycLabelElement
            label={label}
            sequenceNumber={sequenceNumber}
            tip={tip}
            required={required}
          />
        }
        style={style}>
        <KycDirectorsTable
          value={value}
          onChange={newValue => {
            onChange?.(newValue);
          }}
          isAuthorisedSignatory={isAuthorisedSignatory}
        />
      </AsFormItemLayout>
    );
  },
  areEqual,
);
